/** @jsxImportSource ./simboluri-jsx */
// INVENTARUL DE BARE + COSTURILE elementului („aia 2D s-o
// pui sub verificări împreună cu inventarul de bare și costuri").
//
// Extrasul de armătură se cere de pe server FILTRAT pe element
// (`getExtrasArmare(projectId, {element})`) — la un bloc sunt mii de poziții,
// deci nu se aduce tot. Fiecare bară își arată din ce se compune lungimea
// (ancoraj + deschidere + ancoraj), ca în planșa StrCosturiExpert: nu o
// singură cifră opacă.

import { useEffect, useState } from "react";
import { ExtrasArmare } from "./tipuri";
import { getExtrasArmare } from "./apiStructura";
import { bani } from "./bani";
import { CostElement, costElement } from "./costElement";
import { ElementIndexat, nr } from "./elemente";
import { refFaraNivel } from "./legaturi";
import MarcaLink from "./MarcaLink";
import { marcaPozitiei } from "./marci";
import { useT } from "../i18n";

export default function InventarElement({
  projectId,
  cloneId = null,
  el,
  solutie = null,
}: {
  projectId: string;
  cloneId?: string | null;
  el: ElementIndexat;
  /** referința de legătură a rândului privit: cu ea, tipul unei poziții e
   *  un link spre pagina mărcii */
  solutie?: string | null;
}) {
  const t = useT();
  const [extras, setExtras] = useState<ExtrasArmare | null>(null);
  const [eroare, setEroare] = useState(false);
  const [cost, setCost] = useState<CostElement | null>(null);

  useEffect(() => {
    let viu = true;
    setCost(null);
    costElement(projectId, el, cloneId)
      .then((c) => viu && setCost(c))
      .catch(() => undefined);
    return () => {
      viu = false;
    };
    // dependența e referința elementului, nu obiectul (care se reconstruiește)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, cloneId, el.ref, el.tip]);

  useEffect(() => {
    let viu = true;
    setExtras(null);
    setEroare(false);
    // extrasul e indexat pe id-ul de plan FĂRĂ sufixul de nivel la stâlpi
    // (o stivă = un element în extras), cu el la grinzi și ochiuri; un ax de
    // fundare stă acolo sub cheia Pasului 18 («F-u0»), nu sub numele lui
    const cheie = el.tip === "ax"
      ? String(el.brut?.armare?.element ?? `F-${el.ref}`) : el.ref;
    getExtrasArmare(projectId, { element: cheie }, cloneId)
      .then(async (x) => {
        if (!viu) return;
        if ((x.pozitii?.length ?? 0) === 0 && el.tip === "stalp") {
          // a doua încercare, pe stiva întreagă (extrasul poate ține stâlpul
          // ca un singur element, fără sufixul de nivel)
          const y = await getExtrasArmare(projectId, {
            element: refFaraNivel(el.ref),
          }, cloneId);
          if (viu) setExtras(y);
          return;
        }
        setExtras(x);
      })
      .catch(() => viu && setEroare(true));
    return () => {
      viu = false;
    };
  }, [projectId, cloneId, el.ref, el.tip]);

  const pozitii = extras?.pozitii ?? [];
  // ATENȚIE: `extras.kg` / `kg_net` sunt ale extrasului ÎNTREG (serverul
  // filtrează doar lista de poziții, v. `nr_filtrate`) — pe element se
  // însumează pozițiile lui, altfel un card de grindă ar scrie 40 de tone.
  const kgPozitii = pozitii.reduce((s, p) => s + (p.kg ?? 0), 0);
  const kgOtel = pozitii.length
    ? kgPozitii
    : extras?.pe_element?.[el.ref]?.kg ??
      extras?.pe_element?.[`F-${el.ref}`]?.kg ??
      el.otelKg ??
      null;
  const sporPct = extras?.spor_pierderi
    ? Math.round((extras.spor_pierderi - 1) * 100)
    : null;
  const betonM3 =
    el.betonM3 ??
    (typeof el.brut?.beton_m3 === "number" ? el.brut.beton_m3 : null);

  return (
    <div className="ob-card" style={{ overflow: "hidden" }}>
      <div style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <b>{t("struct.inv.titlu")}</b>
        {kgOtel != null && (
          <span className="ob-chip ob-c-mut">{t("struct.inv.kg_otel", { n: nr(kgOtel, 1) })}</span>
        )}
        {betonM3 != null && (
          <span className="ob-chip ob-c-mut">{t("struct.inv.m3_beton", { n: nr(betonM3, 2) })}</span>
        )}
        {extras?.precizie && (
          <span
            className={`ob-chip ${
              extras.precizie === "exact" ? "ob-c-ok" : "ob-c-amb"
            }`}
            title={t("struct.inv.precizie_titlu")}
          >
            {extras.precizie}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {kgOtel != null && betonM3 != null && betonM3 > 0 && (
          <span className="ob-acasa-sub" title={t("struct.inv.kg_m3_titlu")}>
            {t("struct.inv.kg_m3", { n: nr(kgOtel / betonM3) })}
          </span>
        )}
      </div>

      {eroare ? (
        <div className="sg-muted" style={{ padding: "0 14px 12px", fontSize: 12 }}>
          {t("struct.inv.eroare")}
        </div>
      ) : extras == null ? (
        <div className="sg-muted" style={{ padding: "0 14px 12px", fontSize: 12 }}>
          {t("struct.inv.se_aduce")}
        </div>
      ) : pozitii.length === 0 ? (
        <div className="sg-muted" style={{ padding: "0 14px 12px", fontSize: 12 }}>
          {t("struct.inv.fara_pozitii")}
        </div>
      ) : (
        <>
          <table className="ob-sol-tabel">
            <thead>
              <tr>
                <th style={{ width: 46 }}>{t("struct.inv.col_poz")}</th>
                <th style={{ width: 54 }}>{t("struct.pa.col_marca")}</th>
                <th>{t("struct.inv.col_rol")}</th>
                <th style={{ width: 60 }}>Ø</th>
                <th>{t("struct.inv.col_lungime")}</th>
                <th style={{ width: 50, textAlign: "right" }}>{t("struct.inv.col_buc")}</th>
                <th style={{ width: 70, textAlign: "right" }}>{t("struct.inv.col_kg")}</th>
              </tr>
            </thead>
            <tbody>
              {pozitii.slice(0, 40).map((p, i) => (
                <tr key={i}>
                  <td className="sg-mono" style={{ fontSize: 11 }}>{p.poz}</td>
                  <td className="sg-mono" style={{ fontSize: 11 }}>
                    {(() => {
                      const marca = marcaPozitiei(p, el.tip);
                      if (!marca) return <span className="sg-muted">—</span>;
                      return solutie
                        ? <MarcaLink marca={marca} projectId={projectId} solutie={solutie}
                                     cloneId={cloneId} />
                        : marca;
                    })()}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {p.rol}
                    {p.zona ? (
                      <span style={{ color: "var(--mut)" }}> · {p.zona}</span>
                    ) : null}
                  </td>
                  <td className="sg-mono" style={{ fontSize: 12 }}>Ø{p.phi_mm}</td>
                  <td className="sg-mono" style={{ fontSize: 11, color: "var(--mut)" }}>
                    {p.detaliu || `${nr(p.l_bara_m, 2)} m`}
                  </td>
                  <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>
                    {nr(p.n)}×
                  </td>
                  <td className="sg-mono" style={{ textAlign: "right", fontSize: 12 }}>
                    {nr(p.kg, 1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* rețeta de cost a elementului — aceleași cifre ca în drawer-ul
              din Studio (un singur modul: costElement.ts) */}
          {cost && (
            <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                <span className="ob-lbl">{t("struct.inv.cost_titlu")}</span>
                <span style={{ flex: 1 }} />
                <b className="ob-disp" style={{ fontSize: 17 }}>{bani(cost.totalEur)}</b>
              </div>
              {cost.randuri.map((x, i) => (
                <div key={i} className="ob-cost-rand">
                  <span className="sg-mono ob-cost-q">
                    {x.cantitate}
                    {x.derivat && <span className="ob-cost-derivat" title={t("struct.inv.derivat_titlu")}>~</span>}
                  </span>
                  <span className="ob-cost-d">{x.reteta}</span>
                  <span className="sg-mono ob-cost-s">{bani(x.eur)}</span>
                </div>
              ))}
              <div style={{ fontSize: 12, color: "var(--mut)", paddingTop: 6 }}>
                {t("struct.inv.sumar", {
                  material: bani(cost.materialEur),
                  manopera: bani(cost.manoperaEur),
                  sursa: cost.sursa.join(" · "),
                })}
              </div>
            </div>
          )}
          <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)", fontSize: 12, color: "var(--mut)", display: "flex", gap: 10, flexWrap: "wrap" }}>
            {pozitii.length > 40 && (
              <span>{t("struct.inv.inca_pozitii", { n: nr(pozitii.length - 40) })}</span>
            )}
            <span>
              {t("struct.inv.nota_lungimi")}
            </span>
            <span style={{ marginLeft: "auto" }}>
              {t("struct.inv.kg_pe_element", { n: nr(kgPozitii, 1) })}
              {sporPct ? t("struct.inv.pierderi", { n: sporPct }) : ""}
              {extras.parametri?.["pret_otel"] != null
                ? ` · ≈ ${bani(
                    Number(extras.parametri["pret_otel"]) *
                      kgPozitii *
                      (extras.spor_pierderi ?? 1)
                  )}`
                : ""}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
